import { motion } from 'framer-motion';
import { Box, Button, Flex, Stack } from '@mantine/core';
import { useTheme } from '@emotion/react';
import { useIsMutating } from '@tanstack/react-query';
import { forwardRef } from 'react';

import repeat from '~/common/utils/repeat';
import { Text } from '~/domains/common/components/Text/Text';
import dt from '~/testing';

import {
  useAnimationBGColorPickStatus,
  useBGColorPickStatus,
  useBorderColorPickStatus,
  useTextColorPickStatus,
} from '../../hooks/useColorPickStatus';
import { PickVariant } from '../../types/single.pick';
import type { SingleSlate } from '../../types/single.slate';

import PickLiveAnimation from './PickLiveAnimation';
import PickImage from './PickImage';
import usePickStyles from './Pick.styles';

type PickProps = {
  onSlateClick: (id: string) => void;
  variant?: PickVariant;
  shouldMakePicks?: boolean;
} & SingleSlate;

const SCALE_NUMBER = 1.06;
const AnimatedButton = motion(Button);

const Pick = forwardRef<HTMLButtonElement, PickProps>(
  (
    {
      id,
      onSlateClick,
      expectedPicksCount,
      label,
      picks = [],
      variant = PickVariant.PICK_BAR,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    const bgColorPickStatus = useBGColorPickStatus({ ...rest, picks, variant });
    const borderColorPickStatus = useBorderColorPickStatus(rest);
    const textColorPickStatus = useTextColorPickStatus({ ...rest, variant });
    const animationBGColorPickStatus = useAnimationBGColorPickStatus({
      ...rest,
      picks,
    });
    const { classes } = usePickStyles({
      isLive: rest.isLive,
      bgColorPickStatus,
      borderColorPickStatus,
      animationBGColorPickStatus,
      variant,
      isEliminated: rest.isEliminated,
      isUnavailable: rest.isUnavailable,
    });

    const isMakePicksLoading = Boolean(useIsMutating(['makePicks', id]));

    const handlePickClick = (uuid: string): void => {
      if (!rest.isEliminated || !rest.isLocked) {
        onSlateClick(uuid);
      }
    };
    const animatedPropertiesForPickButton = {
      scale: SCALE_NUMBER - picks.length / 100,
      translateY: -4,
      translateZ: 0,
      boxShadow: 'rgba(0, 0, 0, 0.25) 0 0 4px 4px',
    };

    const getMinWidth = () => {
      if (variant === PickVariant.PICK_BAR) return rest.isActive || rest.isSelected ? 100 : 76;
      return 70;
    };

    const emptyPicksLength =
      typeof expectedPicksCount === 'number' ? expectedPicksCount - (picks?.length ?? 0) : 0;

    return (
      <AnimatedButton
        ref={ref}
        {...(variant === PickVariant.PICK_BAR &&
          !rest.isEliminated && {
            whileHover: animatedPropertiesForPickButton,
            whileFocus: animatedPropertiesForPickButton,
            whileTap: animatedPropertiesForPickButton,
          })}
        transition={{
          type: 'ease-in-out',
          duration: 0.35,
        }}
        classNames={classes}
        miw={getMinWidth()}
        radius={theme.radius.sm}
        h="100%"
        onClick={() => {
          if (rest.isUnavailable) {
            return;
          }
          handlePickClick(id);
        }}
      >
        <Stack
          data-test-id={dt.contestDetail.section.myEntries.item.container}
          className={classes.stack}
          spacing={theme.spacing.sm / 3}
          key={id}
        >
          <Box className={classes.imageContainer}>
            <Flex wrap="nowrap" justify="center" gap={theme.spacing.sm / 2}>
              {picks?.map((pick, index) => (
                <Stack key={pick.teamId} align="center" spacing={0}>
                  <PickImage
                    key={pick.teamId}
                    index={index}
                    isLoading={isMakePicksLoading}
                    pick={pick}
                    isActive={rest.isActive}
                    isEliminated={rest.isEliminated}
                    variant={variant}
                  />
                  <Text
                    data-test-id={dt.contestDetail.section.myEntries.item.teamAbbreviation}
                    variant="web-label-x-small"
                    color={variant === PickVariant.MY_ENTRIES ? theme.colors.dark[8] : theme.white}
                  >
                    {pick.teamAlias}
                  </Text>
                </Stack>
              ))}
              {rest?.shouldMakePicks ? (
                <PickImage
                  shouldMakePicks
                  isLoading={isMakePicksLoading}
                  isActive={rest.isActive}
                  isEliminated={rest.isEliminated}
                  variant={variant}
                  expectedPicksCount={expectedPicksCount}
                />
              ) : (
                repeat(emptyPicksLength, (index) => (
                  <PickImage
                    key={index}
                    index={index}
                    isLoading={isMakePicksLoading}
                    isActive={rest.isActive}
                    isEliminated={rest.isEliminated}
                    variant={variant}
                  />
                ))
              )}
            </Flex>
          </Box>
          <Text
            transform="uppercase"
            fz={theme.fontSizes.xs}
            fw={400}
            c={textColorPickStatus}
            ta="center"
          >
            {label}
          </Text>
        </Stack>
        {variant === PickVariant.PICK_BAR && (
          <PickLiveAnimation
            isLive={rest.isLive}
            animationBGColorPickStatus={animationBGColorPickStatus}
          />
        )}
      </AnimatedButton>
    );
  }
);

Pick.displayName = 'Pick';

export { Pick };
